
import { useNavigate } from 'react-router-dom';

interface Feature {
  label: string;
  free: string | boolean;
  premium: string | boolean;
}

const features: Feature[] = [
  { label: 'Business Profile Page', free: true, premium: true },
  { label: 'Category Listing', free: true, premium: true },
  { label: 'Contact Info Display', free: true, premium: true },
  { label: 'Google Maps Integration', free: true, premium: true },
  { label: 'Customer Reviews', free: true, premium: true },
  { label: 'Photo Gallery', free: 'Up to 3', premium: 'Unlimited' },
  { label: 'Business Description', free: 'Basic', premium: 'Rich HTML' },
  { label: 'Featured Badge', free: false, premium: true },
  { label: 'Priority Search Placement', free: false, premium: true },
  { label: 'Homepage Spotlight', free: false, premium: true },
  { label: 'Special Offer Banner', free: false, premium: true },
  { label: 'Social Media Promotion', free: false, premium: true },
  { label: 'Analytics & Insights', free: false, premium: 'Weekly Reports' },
  { label: 'Owner Dashboard', free: 'Basic', premium: 'Full Access' },
  { label: 'Respond to Reviews', free: false, premium: true },
  { label: 'Email Newsletter Feature', free: false, premium: true },
  { label: 'Dedicated Support', free: 'Community', premium: 'Priority' },
];

function CellValue({ value }: { value: string | boolean }) {
  if (value === true) {
    return (
      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 mx-auto">
        <i className="ri-check-line text-sm"></i>
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 mx-auto">
        <i className="ri-close-line text-sm"></i>
      </span>
    );
  }
  return <span className="text-sm font-medium text-gray-700">{value}</span>;
}

export default function ComparisonTable() {
  const navigate = useNavigate();

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full mb-4">
            <i className="ri-scales-3-line text-sm"></i>
            Compare Plans
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Free vs Premium Listing
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto text-sm leading-relaxed">
            Every Roseville business gets a free listing. Upgrade to a premium plan to unlock powerful tools that drive more customers to your door.
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          {/* Table Header */}
          <div className="grid grid-cols-[1fr_140px_140px] bg-gray-50 border-b border-gray-200">
            <div className="px-6 py-4">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Feature</span>
            </div>
            <div className="px-4 py-4 text-center border-l border-gray-200">
              <div className="text-sm font-bold text-gray-900">Free</div>
              <div className="text-xs text-gray-500 mt-0.5">$0 / forever</div>
            </div>
            <div className="px-4 py-4 text-center border-l border-gray-200 bg-teal-50">
              <div className="text-sm font-bold text-teal-700">Premium</div>
              <div className="text-xs text-teal-600 mt-0.5">From $49/mo</div>
            </div>
          </div>

          {/* Table Body */}
          {features.map((feature, idx) => (
            <div
              key={feature.label}
              className={`grid grid-cols-[1fr_140px_140px] items-center ${
                idx !== features.length - 1 ? 'border-b border-gray-100' : ''
              } ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
            >
              <div className="px-6 py-3.5">
                <span className="text-sm text-gray-700">{feature.label}</span>
              </div>
              <div className="px-4 py-3.5 text-center border-l border-gray-100">
                <CellValue value={feature.free} />
              </div>
              <div className="px-4 py-3.5 text-center border-l border-gray-100 bg-teal-50/30">
                <CellValue value={feature.premium} />
              </div>
            </div>
          ))}

          {/* Table Footer / CTA */}
          <div className="grid grid-cols-[1fr_140px_140px] border-t border-gray-200 bg-gray-50">
            <div className="px-6 py-5"></div>
            <div className="px-4 py-5 text-center border-l border-gray-200">
              <a
                href="/submit-business"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/submit-business');
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                List Free
              </a>
            </div>
            <div className="px-4 py-5 text-center border-l border-gray-200 bg-teal-50">
              <a
                href="#inquiry"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Go Premium
                <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          Not sure which plan is right for you?{' '}
          <a href="#inquiry" className="text-teal-600 font-semibold hover:underline cursor-pointer">
            Talk to our team
          </a>{' '}
          — we&apos;re happy to help.
        </p>
      </div>
    </section>
  );
}
